import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { fetchFAQs } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useSEO, faqPageJsonLd, breadcrumbJsonLd, siteUrl } from "@/lib/seo";
import FAQAccordion from "@/components/FAQAccordion";
import { Input } from "@/components/ui/input";
import CTASection from "@/components/CTASection";

const FAQPage = () => {
  const { lang } = useLang();
  const [faqs, setFaqs] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetchFAQs().then(setFaqs).catch(() => {});
  }, []);

  useSEO({
    title: lang === "nl"
      ? "Veelgestelde vragen — Truckwash, HACCP & WaszBase | Wasz.nl"
      : "FAQ — Truckwash, HACCP & WaszBase | Wasz.nl",
    description: lang === "nl"
      ? "Antwoorden op uw vragen over mobiele truckwash, Fleet Cleaning, HACCP-certificaten, NVWA-controles en het WaszBase klantportaal."
      : "Answers to your questions about mobile truckwash, fleet cleaning, HACCP certificates, NVWA inspections and the WaszBase portal.",
    canonical: siteUrl("/faq"),
    lang,
    jsonLd: faqs.length
      ? [
          { id: "faq-page-jsonld", data: faqPageJsonLd(faqs, lang) },
          {
            id: "faq-breadcrumb",
            data: breadcrumbJsonLd([
              { name: "Home", url: siteUrl("/") },
              { name: "FAQ", url: siteUrl("/faq") },
            ]),
          },
        ]
      : [],
  });

  const filtered = faqs.filter((f) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (f.q_nl && f.q_nl.toLowerCase().includes(q)) ||
      (f.q_en && f.q_en.toLowerCase().includes(q)) ||
      (f.a_nl && f.a_nl.toLowerCase().includes(q)) ||
      (f.a_en && f.a_en.toLowerCase().includes(q))
    );
  });

  const grouped = filtered.reduce((acc, f) => {
    (acc[f.category] = acc[f.category] || []).push(f);
    return acc;
  }, {});

  const categoryLabel = (cat) => {
    const map_nl = {
      truckwash: "Truckwash",
      fleet: "Fleet cleaning",
      haccp: "HACCP",
      waszbase: "WaszBase",
      nvwa: "NVWA",
      locations: "Locaties",
      duurzaam: "Duurzaamheid",
    };
    const map_en = {
      truckwash: "Truckwash",
      fleet: "Fleet cleaning",
      haccp: "HACCP",
      waszbase: "WaszBase",
      nvwa: "NVWA",
      locations: "Locations",
      duurzaam: "Sustainability",
    };
    return (lang === "nl" ? map_nl : map_en)[cat] || cat;
  };

  return (
    <main data-testid="faq-page" className="min-h-screen pt-20">
      <section className="mx-auto max-w-4xl px-6 lg:px-8 mb-12">
        <div className="text-center">
          <p className="section-overline mb-3">FAQ</p>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tighter text-white leading-[1.05]">
            {lang === "nl" ? "Veelgestelde vragen" : "Frequently asked questions"}
          </h1>
          <p className="mt-5 text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
            {lang === "nl"
              ? "Antwoorden over truckwash, HACCP, NVWA en het WaszBase portaal. Geen antwoord gevonden? Bel 036 525 5021."
              : "Answers about truckwash, HACCP, NVWA and the WaszBase portal. Not finding what you need? Call +31 36 525 5021."}
          </p>
        </div>
        <div className="relative mt-10 max-w-xl mx-auto">
          <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="faq-search"
            placeholder={lang === "nl" ? "Zoek een vraag…" : "Search a question…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-[#0F0F23] border-white/10 text-white pl-11 h-12 focus-visible:ring-[#00D4FF]"
          />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 lg:px-8 pb-20">
        {Object.keys(grouped).length === 0 && (
          <div className="text-center text-white/50 py-12" data-testid="faq-empty">
            {lang === "nl" ? "Geen resultaten gevonden." : "No results found."}
          </div>
        )}
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="mb-10">
            <h2 className="text-xs uppercase tracking-[0.22em] text-[#00D4FF] font-semibold mb-3">
              {categoryLabel(cat)}
            </h2>
            <FAQAccordion items={items} dataTestid={`faq-cat-${cat}`} />
          </div>
        ))}
      </section>

      <CTASection />
    </main>
  );
};

export default FAQPage;
